import React, { useEffect, useId, useState } from "react";
import PropTypes from "prop-types";
import { Avatar, Box, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { NorthEastRounded } from "@mui/icons-material";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { colors } from "../../theme/colors";
import utils from "../../universal/utils";
import { resolveUserAvatarSrc } from "../../utils/avatarUrl";
import { toProfilePath } from "../../universal/v2Routes";
import {
    isOuroVerifiedAccount,
    MembershipBadge,
    OuroVerifiedBadge,
    resolveMembershipDisplay,
} from "../profile/profileIdentityBadges";
import {
    ACCOUNT_PAGE_SIDEBAR_DIVIDER_SX,
    ACCOUNT_PAGE_SIDEBAR_HEADER_SX,
} from "./accountPageStyles";

const basePath = utils.getEdgeBasePath();

const AVATAR_RING_SX = {
    position: "relative",
    flexShrink: 0,
    width: { xs: "3rem", sm: "3.25rem" },
    height: { xs: "3rem", sm: "3.25rem" },
    borderRadius: "50%",
    padding: "2px",
    background: `conic-gradient(
        from 200deg,
        #F2BE63 0%,
        #FFD27C 12%,
        #D88C25 18%,
        rgba(216,140,37,0) 85%,
        #F2BE63 100%
    )`,
    boxShadow: colors.glow.soft,
};

const FLAG_BADGE_SX = {
    position: "absolute",
    right: "-1px",
    bottom: "-1px",
    width: "1.15rem",
    height: "1.15rem",
    borderRadius: "50%",
    border: `2px solid ${alpha(colors.background.surface, 0.4)}`,
    boxShadow: "0 4px 10px rgba(0, 0, 0, 0.45), 0 0 0 2px rgba(91, 33, 230, 0.25)",
    backgroundColor: alpha(colors.background.surface, 0.8),
    objectFit: "cover",
    zIndex: 2,
};

const PROFILE_LINK_SX = {
    display: "inline-flex",
    alignItems: "center",
    gap: "0.3rem",
    color: colors.text.secondary,
    fontSize: "0.75rem",
    fontWeight: 500,
    lineHeight: 1.2,
    textDecoration: "none",
    transition: "color 0.15s ease-in-out",
    "&:hover": {
        color: colors.accent.main,
    },
    "& .profile-link-arrow": {
        fontSize: "0.85rem",
        transition: "transform 0.15s ease-in-out",
    },
    "&:hover .profile-link-arrow": {
        transform: "translate(1px, -1px)",
    },
};

function AccountSettingsSidebarHeader({
    user,
    membership,
    avatarCacheBust,
    hideDivider = false,
    trailingAction = null,
}) {
    const location = useLocation();
    const verifiedGradientId = `settings-sidebar-verified-${useId().replace(/:/g, "")}`;
    const [countryFlag, setCountryFlag] = useState(user?.flagImageUrl || null);

    const membershipDisplay = resolveMembershipDisplay(
        {
            membershipTier: user?.membershipTier,
            membership: user?.membership || membership,
        },
        { compact: true },
    );
    const nickname = user?.nickname || user?.name || "User";
    const isVerified = isOuroVerifiedAccount(user);
    const profilePath = toProfilePath(user?._id || user?.userId, location.pathname);
    const avatarSrc = resolveUserAvatarSrc({
        basePath,
        user,
        avatarCacheBust,
    });

    useEffect(() => {
        let cancelled = false;

        async function fetchCountryFlag() {
            if (user?.flagImageUrl) {
                setCountryFlag(user.flagImageUrl);
                return;
            }

            if (!user?.country) {
                setCountryFlag(null);
                return;
            }

            try {
                const response = await fetch(
                    `${basePath}/api/v1/lobby/country-flag/${user.country}`,
                );
                const data = await response.json();

                if (!cancelled && data.ok) {
                    setCountryFlag(data.country.flagImageUrl);
                }
            } catch (error) {
                console.error("Failed to fetch country flag:", error);
            }
        }

        fetchCountryFlag();

        return () => {
            cancelled = true;
        };
    }, [user?.country, user?.flagImageUrl]);

    return (
        <>
            <Box sx={ACCOUNT_PAGE_SIDEBAR_HEADER_SX}>
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "flex-end",
                        justifyContent: "flex-end",
                        gap: "0.75rem",
                        width: "100%",
                        mb: { xs: "0.7rem", sm: 0 },
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "flex-end",
                            justifyContent: "center",
                            gap: "0.45rem",
                            flexShrink: 0,
                        }}
                    >
                        {trailingAction}
                    </Box>
                </Box>

                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        gap: { xs: "0.7rem", sm: "0.8rem" },
                        minWidth: 0,
                        width: "calc(100% - 1rem)",
                        ml: "1rem",
                    }}
                >
                    <Box sx={AVATAR_RING_SX}>
                        <Avatar
                            src={avatarSrc}
                            alt={nickname}
                            sx={{
                                width: "100%",
                                height: "100%",
                                border: `2px solid ${colors.background.base}`,
                            }}
                        />
                    </Box>

                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "flex-start",
                            justifyContent: "center",
                            gap: "0.35rem",
                            minWidth: 0,
                            flex: 1,
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: "0.35rem",
                                minWidth: 0,
                                maxWidth: "100%",
                            }}
                        >
                            <Typography
                                component="span"
                                sx={{
                                    color: colors.text.primary,
                                    fontSize: { xs: "0.9rem", sm: "1rem" },
                                    fontWeight: 700,
                                    lineHeight: 1.05,
                                    letterSpacing: "0.01em",
                                    minWidth: 0,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                }}
                            >
                                {nickname}
                            </Typography>
                            {isVerified ? (
                                <OuroVerifiedBadge
                                    gradientId={verifiedGradientId}
                                    size="compact"
                                />
                            ) : null}
                        </Box>

                        {profilePath ? (
                            <Box
                                component={RouterLink}
                                to={profilePath}
                                sx={PROFILE_LINK_SX}
                            >
                                View public profile
                                <NorthEastRounded
                                    className="profile-link-arrow"
                                    aria-hidden
                                />
                            </Box>
                        ) : null}
                    </Box>
                </Box>
            </Box>
            {!hideDivider && (
                <Box aria-hidden="true" sx={ACCOUNT_PAGE_SIDEBAR_DIVIDER_SX} />
            )}
        </>
    );
}

AccountSettingsSidebarHeader.propTypes = {
    user: PropTypes.object,
    membership: PropTypes.object,
    hideDivider: PropTypes.bool,
    trailingAction: PropTypes.node,
    avatarCacheBust: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string,
        PropTypes.object,
    ]),
};

export default React.memo(AccountSettingsSidebarHeader);
